import { useOffline } from '../contexts/OfflineContext';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarDays, ChevronDown, FileDown, Map as MapIcon, Navigation, RefreshCw, Route as RouteIcon, Settings, X } from 'lucide-react';
import { API_URL } from '../config';
import RoutesMap from '../components/RoutesMap';
import RouteScheduleAccordion from '../components/RouteScheduleAccordion';
import { ConfirmModal } from '../components/ConfirmModal';
import ModalShell from '../components/ModalShell';
import yaMapsIcon from '../assets/images/ya_maps___.svg';
import yandexNavigatorIcon from '../assets/images/yandex_navigator_.svg';
import useModalBackButton from '../hooks/useModalBackButton';
import useToast from '../hooks/useToast';
import { fetchProtectedBlob } from '../utils/protectedMedia';
import {
  readRoutesNavigationContext,
  readRoutesReturnDate
} from '../utils/navigationContext';
import {
  OFFLINE_ROUTES_CACHE_UPDATED,
  readOfflineRoutesSnapshot
} from '../utils/offlineRoutesCache';
import { haptic } from '../utils/vibration';
import { getClientVersionHeaders } from '../utils/clientVersion';
import {
  getYandexRoutePoints,
  getYandexNavigatorRouteUrl,
  getYandexRouteUrl,
  YANDEX_MAPS_ROUTE_POINTS_LIMIT
} from '../utils/yandexMaps';

const NO_DATE_GROUP_KEY = '__no_date__';
const YANDEX_ROUTE_START_POINT = { latitude: 57.445300, longitude: 42.096887 };

const formatDisplayDate = (dateKey) => {
  if (dateKey === NO_DATE_GROUP_KEY) return 'Без даты';
  if (!dateKey) return '';
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  const weekdays = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()} · ${weekdays[date.getDay()]}`;
};

const formatRouteSheetDate = (dateKey) => {
  if (dateKey === NO_DATE_GROUP_KEY) return '';
  if (!dateKey) return '';
  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
};

const buildRouteSheetFileName = (dateKey) => `Путевой_лист_${formatRouteSheetDate(dateKey)}.pdf`;

const getDateTone = (dateKey) => {
  if (dateKey === NO_DATE_GROUP_KEY) {
    return {
      border: 'border-slate-200 dark:border-gray-800',
      badge: 'bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300'
    };
  }

  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  if (dateKey === todayKey) {
    return {
      border: 'border-green-200 dark:border-green-900',
      badge: 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300'
    };
  }
  if (dateKey < todayKey) {
    return {
      border: 'border-red-200 dark:border-red-900',
      badge: 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-300'
    };
  }
  return {
    border: 'border-blue-200 dark:border-blue-900',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300'
  };
};

const filterRouteItemsForDisplay = (items, showTasksInRoutes) => {
  const source = Array.isArray(items) ? items : [];
  return showTasksInRoutes
    ? source
    : source.filter((item) => item?.entity_type !== 'task');
};

const getEmptyRoutesMessage = (routeScope) => {
  if (routeScope === 'mine') {
    return {
      title: 'Для тебя сейчас нет назначенных маршрутов',
      subtitle: 'Попробуй переключиться на показ всех доступных маршрутов.'
    };
  }

  return {
    title: 'Пока нет дат с назначенными маршрутами',
    subtitle: 'Здесь появятся выездные этапы, когда они будут назначены.'
  };
};

const ListLoadingSkeleton = () => (
  <div className="space-y-3">
    {[0, 1].map((groupIndex) => (
      <div
        key={groupIndex}
        className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900"
      >
        <div className="flex items-center justify-between bg-slate-50 px-4 py-3 dark:bg-gray-900/80">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-4 w-52 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-32 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
          </div>
          <div className="ml-3 flex items-center gap-2">
            <div className="h-6 w-8 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-4 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
          </div>
        </div>

        <div className="space-y-2 bg-slate-50 px-2 pb-2 pt-2 sm:px-3 sm:pb-3 dark:bg-gray-950">
          {[0, 1, 2].map((cardIndex) => (
            <div
              key={cardIndex}
              className="rounded-xl border border-gray-200 bg-white px-3 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-900"
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="h-7 w-7 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="h-6 w-28 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="h-6 w-20 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
              </div>

              <div className="mt-2 flex items-start gap-2">
                <div className="mt-0.5 h-3.5 w-3.5 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="h-3 w-40 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
                  <div className="h-3 w-56 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
                </div>
              </div>

              <div className="mt-2 flex items-start gap-2">
                <div className="mt-0.5 h-3.5 w-3.5 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="h-3 w-full animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
                  <div className="h-3 w-3/4 animate-pulse rounded-full bg-gray-100 dark:bg-gray-800" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const RoutesPage = () => {
  const offline = useOffline();
  const connectionUnavailable = ['offline', 'server_unavailable'].includes(offline?.networkStatus);
  const location = useLocation();
  const returnDateFromUrl = readRoutesReturnDate(location.search);
  const restoredRouteContextRef = useRef(readRoutesNavigationContext());
  const restoredListDateKeyRef = useRef(
    returnDateFromUrl
      || restoredRouteContextRef.current?.expandedDateKey
      || restoredRouteContextRef.current?.selectedDateKey
      || ''
  );
  const activeDateKeyRef = useRef(
    returnDateFromUrl
      || restoredRouteContextRef.current?.expandedDateKey
      || restoredRouteContextRef.current?.selectedDateKey
      || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('routes_last_open_date') : '')
      || ''
  );
  const restoredListDateAppliedRef = useRef(false);
  const restoredListDateVisibleRef = useRef(false);
  const pollingRequestRef = useRef(null);
  const latestRoutesRequestIdRef = useRef(0);
  const [activeTab, setActiveTab] = useState(() => restoredRouteContextRef.current?.activeTab || 'list');
  const [loading, setLoading] = useState(false);
  const [routeItems, setRouteItems] = useState([]);
  const [offlineRoutesSnapshot, setOfflineRoutesSnapshot] = useState(null);
  const [usingOfflineRoutes, setUsingOfflineRoutes] = useState(false);
  const [expandedDates, setExpandedDates] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(() => localStorage.getItem('routes_auto_refresh') !== 'false');
  const [routeScope, setRouteScope] = useState(() => localStorage.getItem('routes_scope') || 'mine');
  const [showTasksInRoutes, setShowTasksInRoutes] = useState(() => localStorage.getItem('routes_show_tasks') !== 'false');
  const [departmentView, setDepartmentView] = useState(() => localStorage.getItem('routes_department_view') || 'grouped');
  const [showDepartmentBadgeInMixed, setShowDepartmentBadgeInMixed] = useState(() => localStorage.getItem('routes_show_department_badge') !== 'false');
  const [showExecutorInList, setShowExecutorInList] = useState(() => localStorage.getItem('routes_show_executor_in_list') !== 'false');
  const [hideRegionInAddress, setHideRegionInAddress] = useState(() => localStorage.getItem('routes_hide_region_in_address') === 'true');
  const [currentUserId, setCurrentUserId] = useState(() => Number(localStorage.getItem('user_id')) || null);
  const [currentUserType, setCurrentUserType] = useState(() => localStorage.getItem('user_type') || null);
  const [openMapDatePicker, setOpenMapDatePicker] = useState(() => () => {});
  const [downloadingRouteSheetDate, setDownloadingRouteSheetDate] = useState(null);
  const [routeConfirmRequest, setRouteConfirmRequest] = useState(null);
  const [routeNavigationModal, setRouteNavigationModal] = useState(null);
  const { toast, showToast, hideToast } = useToast(3200);
  const [mapFocusRequest, setMapFocusRequest] = useState(() => {
    const restored = restoredRouteContextRef.current;
    const focusOrderId = restored?.focusOrderId;
    const focusRouteKey = restored?.focusRouteKey;
    if (restored?.activeTab !== 'map' || (!focusOrderId && !focusRouteKey)) return null;
    return {
      orderId: focusOrderId,
      entityType: restored?.focusEntityType || '',
      routeKey: focusRouteKey || '',
      scheduledDateKey: restored.selectedDateKey || '',
      issuedAt: Date.now()
    };
  });
  useModalBackButton(showSettings, () => setShowSettings(false));
  useModalBackButton(!!routeConfirmRequest, () => setRouteConfirmRequest(null));
  useModalBackButton(!!routeNavigationModal, () => setRouteNavigationModal(null));

  const applyOfflineSnapshotToRoutes = useCallback((snapshot) => {
    if (!snapshot) return false;
    setOfflineRoutesSnapshot(snapshot);
    const effectiveUserId = Number(currentUserId || snapshot.userId || localStorage.getItem('user_id'));
    const allItems = Array.isArray(snapshot.items) ? snapshot.items : [];
    const effectiveScope = routeScope === 'mine'
      ? 'mine'
      : (routeScope === 'available' || snapshot?.preferences?.scope === 'available')
        ? 'available'
        : 'all';
    const visibleItems = effectiveScope === 'mine'
      ? allItems.filter(item => Number(item.assigned_to) === effectiveUserId)
      : effectiveScope === 'available'
        ? allItems.filter(item => !item.assigned_to || Number(item.assigned_to) === effectiveUserId)
        : allItems;
    setRouteItems(filterRouteItemsForDisplay(visibleItems, showTasksInRoutes));
    setUsingOfflineRoutes(true);
    return true;
  }, [routeScope, showTasksInRoutes, currentUserId]);

  const loadCachedOfflineRoutes = useCallback(async () => {
    try {
      const snapshot = await readOfflineRoutesSnapshot(currentUserId);
      if (!snapshot) return null;
      setOfflineRoutesSnapshot(snapshot);
      return snapshot;
    } catch (error) {
      console.warn('Не удалось прочитать офлайн-кэш маршрутов:', error);
      return null;
    }
  }, [currentUserId]);

  const loadRoutes = useCallback(async (signal, { silent = false } = {}) => {
    const requestId = latestRoutesRequestIdRef.current + 1;
    latestRoutesRequestIdRef.current = requestId;

    let hasCached = false;
    if (!silent) {
      // Сначала пытаемся мгновенно применить локальный офлайн-кэш (без скелетона)
      try {
        const snapshot = await loadCachedOfflineRoutes();
        if (snapshot && requestId === latestRoutesRequestIdRef.current) {
          if (applyOfflineSnapshotToRoutes(snapshot)) {
            hasCached = true;
          }
        }
      } catch (cacheErr) {
        // ignore
      }
      if (!hasCached) {
        setLoading(true);
      }
    }

    try {
      if (connectionUnavailable || navigator.onLine === false) throw new Error('Нет соединения с сервером');
      const params = new URLSearchParams();
      if (routeScope === 'mine') {
        params.set('scope', 'mine');
      }
      if (showTasksInRoutes) {
        params.set('include_tasks', 'true');
      }

      const response = await fetch(`${API_URL}/api/orders/route-schedule-calendar${params.toString() ? `?${params.toString()}` : ''}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        signal
      });

      if (!response.ok) {
        throw new Error('Не удалось загрузить маршруты');
      }

      const data = await response.json();
      if (requestId === latestRoutesRequestIdRef.current) {
        setRouteItems(Array.isArray(data) ? data : []);
        setUsingOfflineRoutes(false);
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Ошибка загрузки маршрутов:', error);
      if (!silent && requestId === latestRoutesRequestIdRef.current && !hasCached) {
        const snapshot = await loadCachedOfflineRoutes();
        if (snapshot && applyOfflineSnapshotToRoutes(snapshot)) {
          return;
        }
        setRouteItems([]);
      }
    } finally {
      if (!silent && requestId === latestRoutesRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [applyOfflineSnapshotToRoutes, loadCachedOfflineRoutes, routeScope, showTasksInRoutes, connectionUnavailable]);

  useEffect(() => {
    const controller = new AbortController();
    loadRoutes(controller.signal);
    return () => {
      controller.abort();
    };
  }, [loadRoutes]);

  useEffect(() => {
    if (!autoRefresh) return undefined;

    const runSilentRefresh = () => {
      if (document.hidden || connectionUnavailable || navigator.onLine === false) return;
      pollingRequestRef.current?.abort?.();
      const controller = new AbortController();
      pollingRequestRef.current = controller;
      loadRoutes(controller.signal, { silent: true });
    };

    const intervalId = window.setInterval(runSilentRefresh, 30000);
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        runSilentRefresh();
      }
    };
    const handleOnline = () => {
      runSilentRefresh();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
      pollingRequestRef.current?.abort?.();
      pollingRequestRef.current = null;
    };
  }, [loadRoutes, connectionUnavailable, autoRefresh]);

  useEffect(() => {
    loadCachedOfflineRoutes();
  }, [loadCachedOfflineRoutes]);

  useEffect(() => {
    const handleOffline = async () => {
      const snapshot = await loadCachedOfflineRoutes();
      if (snapshot) {
        applyOfflineSnapshotToRoutes(snapshot);
      }
    };

    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('offline', handleOffline);
    };
  }, [applyOfflineSnapshotToRoutes, loadCachedOfflineRoutes, routeScope]);

  useEffect(() => {
    const handleCacheUpdated = (event) => {
      if (Number(event.detail?.userId) !== Number(currentUserId || localStorage.getItem('user_id'))) return;
      setOfflineRoutesSnapshot(event.detail);
      if (usingOfflineRoutes) applyOfflineSnapshotToRoutes(event.detail);
    };
    window.addEventListener(OFFLINE_ROUTES_CACHE_UPDATED, handleCacheUpdated);
    return () => window.removeEventListener(OFFLINE_ROUTES_CACHE_UPDATED, handleCacheUpdated);
  }, [currentUserId, usingOfflineRoutes, routeScope, applyOfflineSnapshotToRoutes]);

  useEffect(() => {
    if (usingOfflineRoutes && offlineRoutesSnapshot) {
      applyOfflineSnapshotToRoutes(offlineRoutesSnapshot);
    }
  }, [routeScope, usingOfflineRoutes, offlineRoutesSnapshot, applyOfflineSnapshotToRoutes]);

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const response = await fetch(`${API_URL}/api/check-auth`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            ...getClientVersionHeaders()
          }
        });
        if (!response.ok) return;
        const data = await response.json();
        setCurrentUserId(Number(data.user_id) || null);
        setCurrentUserType(data.user_type || null);
      } catch (error) {
        console.error('Не удалось загрузить текущего пользователя для маршрутов:', error);
      }
    };

    loadCurrentUser();
  }, []);

  useEffect(() => {
    localStorage.setItem('routes_auto_refresh', autoRefresh ? 'true' : 'false');
  }, [autoRefresh]);

  useEffect(() => {
    localStorage.setItem('routes_scope', routeScope);
  }, [routeScope]);

  useEffect(() => {
    localStorage.setItem('routes_show_tasks', showTasksInRoutes ? 'true' : 'false');
  }, [showTasksInRoutes]);

  useEffect(() => {
    localStorage.setItem('routes_department_view', departmentView);
  }, [departmentView]);

  useEffect(() => {
    localStorage.setItem('routes_show_department_badge', showDepartmentBadgeInMixed ? 'true' : 'false');
  }, [showDepartmentBadgeInMixed]);

  useEffect(() => {
    localStorage.setItem('routes_show_executor_in_list', showExecutorInList ? 'true' : 'false');
  }, [showExecutorInList]);

  useEffect(() => {
    localStorage.setItem('routes_hide_region_in_address', hideRegionInAddress ? 'true' : 'false');
  }, [hideRegionInAddress]);

  const downloadRouteSheet = useCallback(async (dateKey) => {
    if (!dateKey || downloadingRouteSheetDate) return;
    haptic('selection');
    setDownloadingRouteSheetDate(dateKey);
    try {
      const params = new URLSearchParams({
        target_date: dateKey,
        scope: 'mine'
      });
      const blob = await fetchProtectedBlob(`${API_URL}/api/orders/route-sheet-pdf?${params.toString()}`);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = buildRouteSheetFileName(dateKey);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      showToast(`Путевой лист на ${formatRouteSheetDate(dateKey)} скачан`, 'success');
    } catch (error) {
      showToast(error.message || 'Не удалось скачать путевой лист', 'error');
    } finally {
      setDownloadingRouteSheetDate(null);
    }
  }, [downloadingRouteSheetDate, showToast]);

  const openRouteInYandexMaps = useCallback((group) => {
    const items = Array.isArray(group?.items) ? group.items : [];
    const availablePoints = getYandexRoutePoints(items);
    if (availablePoints.length < 1) {
      showToast('Для маршрута нужен хотя бы один адрес с координатами', 'warning');
      return;
    }

    const includedDestinationPoints = availablePoints.slice(0, YANDEX_MAPS_ROUTE_POINTS_LIMIT - 1);
    const includedPoints = [YANDEX_ROUTE_START_POINT, ...includedDestinationPoints];
    const url = getYandexNavigatorRouteUrl(includedPoints);
    if (!url) {
      showToast('Не удалось сформировать ссылку на маршрут', 'error');
      return;
    }

    const request = {
      dateKey: group.dateKey,
      url,
      mapsUrl: getYandexRouteUrl(includedPoints),
      includedCount: includedDestinationPoints.length,
      availableCount: availablePoints.length,
      missingCoordinatesCount: Math.max(0, items.length - availablePoints.length),
      truncatedCount: Math.max(0, availablePoints.length - includedPoints.length)
    };

    haptic('selection');
    setRouteConfirmRequest(request);
  }, [showToast]);

  const confirmRouteNavigation = useCallback((target = 'navigator') => {
    const request = routeConfirmRequest;
    if (!request) return;
    const selectedRequest = {
      ...request,
      url: target === 'maps' ? request.mapsUrl : request.url
    };
    setRouteConfirmRequest(null);
    if (selectedRequest.truncatedCount > 0 || selectedRequest.missingCoordinatesCount > 0) {
      setRouteNavigationModal(selectedRequest);
      return;
    }
    haptic('open');
    window.open(selectedRequest.url, '_blank', 'noopener,noreferrer');
  }, [routeConfirmRequest]);

  const groupedByDate = useMemo(() => {
    const groups = new Map();
    routeItems.forEach((item) => {
      const key = item.scheduled_date_key || NO_DATE_GROUP_KEY;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(item);
    });
    return Array.from(groups.entries())
      .sort(([aKey], [bKey]) => {
        if (aKey === NO_DATE_GROUP_KEY) return 1;
        if (bKey === NO_DATE_GROUP_KEY) return -1;
        return aKey.localeCompare(bKey);
      })
      .map(([dateKey, items]) => ({
        dateKey,
        items: [...items].sort((a, b) => {
          const timeA = a.time_slot || '';
          const timeB = b.time_slot || '';
          const slotCompare = timeA.localeCompare(timeB);
          if (slotCompare !== 0) return slotCompare;
          return String(a.order_number || '').localeCompare(String(b.order_number || ''));
        })
      }));
  }, [routeItems]);

  const emptyState = useMemo(() => getEmptyRoutesMessage(routeScope), [routeScope]);

  const handleToggleDate = useCallback((dateKey) => {
    haptic('tap');
    setExpandedDates((prev) => {
      const willOpen = !prev[dateKey];
      const nextActiveKey = willOpen ? dateKey : '';
      activeDateKeyRef.current = nextActiveKey;
      try {
        if (nextActiveKey) {
          sessionStorage.setItem('routes_last_open_date', nextActiveKey);
        } else {
          sessionStorage.removeItem('routes_last_open_date');
        }
      } catch {
        // ignore
      }

      const next = {};
      groupedByDate.forEach((entry) => {
        next[entry.dateKey] = willOpen && entry.dateKey === dateKey;
      });
      return next;
    });
  }, [groupedByDate]);

  useEffect(() => {
    if (
      returnDateFromUrl
      && restoredListDateKeyRef.current !== returnDateFromUrl
    ) {
      restoredListDateKeyRef.current = returnDateFromUrl;
      activeDateKeyRef.current = returnDateFromUrl;
      restoredListDateAppliedRef.current = false;
      restoredListDateVisibleRef.current = false;
    }

    const knownKeys = groupedByDate.map((group) => group.dateKey);
    if (knownKeys.length === 0) return;

    let appliedSavedDate = false;
    setExpandedDates((prev) => {
      const savedDateKey = restoredListDateKeyRef.current;
      const savedDateApplied = restoredListDateAppliedRef.current;
      const shouldApplySavedDate = Boolean(
        !savedDateApplied
        && savedDateKey
        && knownKeys.includes(savedDateKey)
      );

      let activeKey = null;

      if (shouldApplySavedDate) {
        activeKey = savedDateKey;
        appliedSavedDate = true;
      } else {
        const openInPrev = knownKeys.find((key) => prev[key]);
        const rememberedKey = activeDateKeyRef.current
          || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('routes_last_open_date') : '');

        if (openInPrev && knownKeys.includes(openInPrev)) {
          activeKey = openInPrev;
        } else if (rememberedKey && knownKeys.includes(rememberedKey)) {
          activeKey = rememberedKey;
        } else if (activeDateKeyRef.current === '' && Object.keys(prev).length > 0) {
          activeKey = null;
        } else {
          activeKey = knownKeys[0] || null;
        }
      }

      if (activeKey) {
        activeDateKeyRef.current = activeKey;
        try {
          sessionStorage.setItem('routes_last_open_date', activeKey);
        } catch {
          // ignore
        }
      }

      const next = {};
      knownKeys.forEach((key) => {
        next[key] = Boolean(activeKey && key === activeKey);
      });
      return next;
    });

    if (appliedSavedDate) {
      restoredListDateAppliedRef.current = true;
    }
  }, [groupedByDate, returnDateFromUrl]);

  useEffect(() => {
    const savedDateKey = restoredListDateKeyRef.current;
    if (
      !savedDateKey
      || restoredListDateVisibleRef.current
      || !restoredListDateAppliedRef.current
      || !expandedDates[savedDateKey]
      || loading
    ) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      const dateSection = Array.from(document.querySelectorAll('[data-route-date-key]'))
        .find((element) => element.dataset.routeDateKey === savedDateKey);
      if (!dateSection) return;

      dateSection.scrollIntoView({ block: 'start', behavior: 'auto' });
      restoredListDateVisibleRef.current = true;
    }, 280);

    return () => window.clearTimeout(timeoutId);
  }, [expandedDates, loading]);

  const effectiveCurrentUserId = currentUserId || offlineRoutesSnapshot?.userId || null;
  const effectiveCurrentUserType = currentUserType || offlineRoutesSnapshot?.userType || (typeof localStorage !== 'undefined' ? localStorage.getItem('user_type') : '') || 'user';

  return (
    <div className="flex w-full flex-col gap-4 desktop-content-container-wide">
      <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-xs dark:border-gray-800/80 dark:bg-gray-900">
        <div className="grid grid-cols-2 border-b border-gray-200/80 dark:border-gray-800/80">
          <button
            type="button"
            onClick={() => setActiveTab('list')}
            className={`flex items-center justify-center gap-2 border-b-2 px-2 py-3 text-sm font-medium transition-colors ${
              activeTab === 'list'
                ? 'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
            }`}
          >
            <RouteIcon className="h-4 w-4 shrink-0" />
            <span>Список</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('map')}
            className={`flex items-center justify-center gap-2 border-b-2 px-2 py-3 text-sm font-medium transition-colors ${
              activeTab === 'map'
                ? 'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
            }`}
          >
            <MapIcon className="h-4 w-4 shrink-0" />
            <span>Карта</span>
          </button>
        </div>
        <div className="flex items-center justify-between gap-3 px-3 py-2.5">
          <div className="grid grid-cols-2 gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
            <button
              type="button"
              onClick={() => setRouteScope('mine')}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                routeScope === 'mine'
                  ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              Мои
            </button>
            <button
              type="button"
              onClick={() => setRouteScope('all')}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                routeScope === 'all'
                  ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              Все
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => loadRoutes(undefined, { silent: false })}
              disabled={loading}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              title="Обновить маршруты"
              aria-label="Обновить маршруты"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {activeTab === 'map' && (
              <button
                type="button"
                onClick={() => openMapDatePicker()}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                title="Выбрать дату на карте"
                aria-label="Выбрать дату на карте"
              >
                <CalendarDays className="h-4 w-4" />
              </button>
            )}
            {activeTab === 'list' && (
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                title="Настройки списка"
                aria-label="Настройки списка"
              >
                <Settings className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {activeTab === 'map' ? (
        <RoutesMap
          items={routeItems}
          loading={loading}
          currentUserId={effectiveCurrentUserId}
          currentUserType={effectiveCurrentUserType}
          onRegisterOpenDatePicker={setOpenMapDatePicker}
          focusRequest={mapFocusRequest}
          routeContextBase={{
            activeTab: 'map',
            routeScope,
            showTasksInRoutes,
            departmentView,
            showDepartmentBadgeInMixed,
            showExecutorInList,
            hideRegionInAddress
          }}
        />
      ) : loading ? (
        <ListLoadingSkeleton />
      ) : groupedByDate.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500 shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
          <div className="font-medium text-gray-700 dark:text-gray-200">{emptyState.title}</div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{emptyState.subtitle}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {groupedByDate.map((group) => {
            const tone = getDateTone(group.dateKey);
            return (
              <div
                key={group.dateKey}
                data-route-date-key={group.dateKey}
                className={`overflow-hidden rounded-2xl border bg-white shadow-sm dark:bg-gray-900 scroll-mt-16 ${tone.border}`}
              >
                <div className="flex items-center gap-2 bg-slate-50 px-4 py-3 dark:bg-gray-900/80">
                  <button
                    type="button"
                    onClick={() => handleToggleDate(group.dateKey)}
                    className="min-w-0 text-left"
                    aria-expanded={Boolean(expandedDates[group.dateKey])}
                  >
                    <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {formatDisplayDate(group.dateKey)}
                    </span>
                  </button>
                  {routeScope === 'mine' && group.dateKey !== NO_DATE_GROUP_KEY && (
                    <div className="flex shrink-0 items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => downloadRouteSheet(group.dateKey)}
                        disabled={downloadingRouteSheetDate === group.dateKey}
                        className="inline-flex h-8 w-8 min-w-[32px] shrink-0 aspect-square items-center justify-center rounded-full border border-blue-100 bg-blue-50 p-0 text-blue-600 transition-colors hover:border-blue-200 hover:bg-blue-100 disabled:opacity-50 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-950/50"
                        style={{ width: 32, minWidth: 32, maxWidth: 32, height: 32, minHeight: 32, maxHeight: 32, flex: '0 0 32px', borderRadius: '50%', padding: 0 }}
                        title={`Скачать путевой лист на ${formatRouteSheetDate(group.dateKey)}`}
                        aria-label={`Скачать путевой лист на ${formatRouteSheetDate(group.dateKey)}`}
                      >
                        <FileDown className={`h-3.5 w-3.5 ${downloadingRouteSheetDate === group.dateKey ? 'animate-pulse' : ''}`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => openRouteInYandexMaps(group)}
                        className="inline-flex h-8 w-8 min-w-[32px] shrink-0 aspect-square items-center justify-center rounded-full border border-red-100 bg-red-50 p-0 text-red-600 transition-colors hover:border-red-200 hover:bg-red-100 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300 dark:hover:bg-red-950/50"
                        style={{ width: 32, minWidth: 32, maxWidth: 32, height: 32, minHeight: 32, maxHeight: 32, flex: '0 0 32px', borderRadius: '50%', padding: 0 }}
                        title={`Отправить маршрут на ${formatRouteSheetDate(group.dateKey)} в навигатор`}
                        aria-label={`Отправить маршрут на ${formatRouteSheetDate(group.dateKey)} в навигатор`}
                      >
                        <Navigation className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => handleToggleDate(group.dateKey)}
                    className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-lg px-1 py-1"
                    aria-label={expandedDates[group.dateKey] ? 'Свернуть дату' : 'Развернуть дату'}
                    aria-expanded={Boolean(expandedDates[group.dateKey])}
                  >
                    <span className={`inline-flex h-6 min-w-[24px] items-center justify-center rounded-full px-2 text-xs font-bold ${tone.badge}`}>
                      {group.items.length}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform dark:text-gray-300 ${expandedDates[group.dateKey] ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                <AnimatePresence initial={false}>
                  {expandedDates[group.dateKey] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden px-2 pb-2 sm:px-3 sm:pb-3"
                    >
                      <RouteScheduleAccordion
                        title="Выезды"
                        selectedDate={group.dateKey}
                        items={group.items}
                        open
                        onToggle={() => {}}
                        hideOuterHeader
                        groupByDepartment={departmentView === 'grouped'}
                        showDepartmentOnCard={departmentView === 'mixed'}
                        showDepartmentBadge={departmentView === 'mixed' && showDepartmentBadgeInMixed}
                         showExecutorInCard={showExecutorInList}
                         showStatusBadge={false}
                         showUnassignedStatusBadge={false}
                        hideRegionInAddress={hideRegionInAddress}
                        currentUserId={effectiveCurrentUserId}
                        currentUserType={effectiveCurrentUserType}
                        routeContextBase={{
                          activeTab: 'list',
                          routeScope,
                          showTasksInRoutes,
                          departmentView,
                          showDepartmentBadgeInMixed,
                          showExecutorInList,
                          hideRegionInAddress,
                          expandedDateKey: group.dateKey
                        }}
                        onShowOnMap={(item) => {
                          setMapFocusRequest({
                            orderId: item.order_id,
                            entityType: item.entity_type || 'order',
                            routeKey: `${item.entity_type || 'order'}:${item.order_id}`,
                            scheduledDateKey: item.scheduled_date_key || '',
                            issuedAt: Date.now()
                          });
                          setActiveTab('map');
                        }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        isOpen={!!routeConfirmRequest}
        title="Построить маршрут дня?"
        message={routeConfirmRequest
          ? `Выберите приложение для маршрута на ${formatRouteSheetDate(routeConfirmRequest.dateKey)}: начальная точка и ${routeConfirmRequest.availableCount} адрес${routeConfirmRequest.availableCount === 1 ? '' : routeConfirmRequest.availableCount < 5 ? 'а' : 'ов'}.`
          : ''}
        confirmText="Навигатор"
        alternativeText="Карты"
        confirmIcon={<img src={yandexNavigatorIcon} alt="" className="h-5 w-5 object-contain" />}
        alternativeIcon={<img src={yaMapsIcon} alt="" className="h-5 w-5 object-contain" />}
        cancelText="Отмена"
        type="info"
        onConfirm={() => confirmRouteNavigation('navigator')}
        onAlternative={() => confirmRouteNavigation('maps')}
        onCancel={() => setRouteConfirmRequest(null)}
      />

      {routeNavigationModal && (
        <ModalShell
          open={Boolean(routeNavigationModal)}
          onClose={() => {
            haptic('close');
            setRouteNavigationModal(null);
          }}
          variant="window"
          panelClassName="w-full max-w-md p-4"
        >
          <div className="mb-4 flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-300">
              <Navigation className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">В маршрут войдут не все адреса</h3>
              <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-400">
                На {formatRouteSheetDate(routeNavigationModal.dateKey)} найдено адресов с координатами: {routeNavigationModal.availableCount}. В Яндекс Карты уйдут первые {routeNavigationModal.includedCount} адресов после начальной точки.
              </p>
              {routeNavigationModal.truncatedCount > 0 && (
                <p className="mt-2 text-sm leading-5 text-gray-600 dark:text-gray-400">
                  За один раз отправляем до {YANDEX_MAPS_ROUTE_POINTS_LIMIT} точек: начальная точка плюс до {YANDEX_MAPS_ROUTE_POINTS_LIMIT - 1} адресов. Не войдут: {routeNavigationModal.truncatedCount}.
                </p>
              )}
              {routeNavigationModal.missingCoordinatesCount > 0 && (
                <p className="mt-2 text-sm leading-5 text-gray-600 dark:text-gray-400">
                  Без координат: {routeNavigationModal.missingCoordinatesCount}. Эти адреса тоже не попадут в маршрут.
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                haptic('close');
                setRouteNavigationModal(null);
              }}
              className="min-h-[40px] flex-1 rounded-lg bg-gray-100 px-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => {
                haptic('open');
                window.open(routeNavigationModal.url, '_blank', 'noopener,noreferrer');
                setRouteNavigationModal(null);
              }}
              className="min-h-[40px] flex-1 rounded-lg bg-blue-600 px-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
            >
              Открыть маршрут
            </button>
          </div>
        </ModalShell>
      )}

      {showSettings && (
        <ModalShell
          open={showSettings}
          onClose={() => {
            haptic('close');
            setShowSettings(false);
          }}
          variant="window"
          panelClassName="flex max-h-[78dvh] w-full max-w-md flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
            <div>
              <div className="text-base font-semibold text-gray-900 dark:text-gray-100">Настройки списка</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Только для вкладки списка</div>
            </div>
            <button
              type="button"
              aria-label="Закрыть настройки"
              onClick={() => {
                haptic('close');
                setShowSettings(false);
              }}
              className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto p-4">
            <button
              type="button"
              onClick={() => {
                haptic('selection');
                setAutoRefresh((prev) => !prev);
              }}
              className="flex w-full items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800/50"
            >
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Автообновление маршрутов
              </span>
              <span
                className="toggle-switch shrink-0"
                style={{
                  '--toggle-bg': autoRefresh ? '#2563eb' : '#d1d5db',
                  '--knob-x': autoRefresh ? '22px' : '2px'
                }}
                aria-hidden="true"
              >
                <span className="toggle-switch-knob" />
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                haptic('selection');
                setShowTasksInRoutes((prev) => !prev);
              }}
              className="flex w-full items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800/50"
            >
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Показывать поручения
              </span>
              <span
                className="toggle-switch shrink-0"
                style={{
                  '--toggle-bg': showTasksInRoutes ? '#2563eb' : '#d1d5db',
                  '--knob-x': showTasksInRoutes ? '22px' : '2px'
                }}
                aria-hidden="true"
              >
                <span className="toggle-switch-knob" />
              </span>
            </button>

            <div>
              <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Как показывать подразделения</div>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setDepartmentView('grouped');
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    departmentView === 'grouped'
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  По подразделениям
                </button>
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setDepartmentView('mixed');
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    departmentView === 'mixed'
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Общий список
                </button>
              </div>
            </div>

            {departmentView === 'mixed' && (
              <div>
                <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Показывать подразделение в карточке</div>
                <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                  <button
                    type="button"
                    onClick={() => {
                      haptic('selection');
                      setShowDepartmentBadgeInMixed(true);
                    }}
                    className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      showDepartmentBadgeInMixed
                        ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    Показывать
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      haptic('selection');
                      setShowDepartmentBadgeInMixed(false);
                    }}
                    className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      !showDepartmentBadgeInMixed
                        ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    Скрывать
                  </button>
                </div>
              </div>
            )}

            <div>
              <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Показывать исполнителя в карточке</div>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setShowExecutorInList(true);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    showExecutorInList
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Показывать
                </button>
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setShowExecutorInList(false);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    !showExecutorInList
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Скрывать
                </button>
              </div>
            </div>

            <div>
              <div className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">Показывать область в адресе</div>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setHideRegionInAddress(false);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    !hideRegionInAddress
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Показывать
                </button>
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    setHideRegionInAddress(true);
                  }}
                  className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    hideRegionInAddress
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  Скрывать
                </button>
              </div>
            </div>
          </div>
        </ModalShell>
      )}
          </div>
  );
};

export default RoutesPage;
